import { Outlet, Link, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown } from "lucide-react";
import { WhatsAppIcon } from "@/components/PageCTA";

const LOGO_URL = "/images/logo-bee-white.png";

const mainNav = [
  { name: "Cennik", href: "/pakiety" },
  { name: "Portfolio", href: "/realizacje" },
  { name: "Blog", href: "/blog" },
  { name: "FAQ", href: "/faq" },
  { name: "Kontakt", href: "/kontakt" },
];

const ofertaItems = [
  { name: "Strony WWW", href: "/strony-www" },
  { name: "Grafiki hodowlane", href: "/grafiki-reklamowe" },
  { name: "Logo i branding", href: "/logo-branding" },
  { name: "Materiały reklamowe", href: "/odziez-z-nadrukiem" },
  { name: "Wizytówki Google", href: "/wizytowki-google" },
];

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [ofertaOpen, setOfertaOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setOfertaOpen(false);
  }, [location]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
  }, [menuOpen]);

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? "bg-[var(--color-black)]/95 backdrop-blur-md border-b border-[var(--color-border-dark)]"
            : "bg-transparent"
        }`}
        data-testid="main-header"
      >
        <div className="container py-5 flex items-center justify-between">
          <Link to="/" className="relative z-50 flex items-center gap-3" data-testid="logo-link">
            <img src={LOGO_URL} alt="ApisDesign" className="h-9 md:h-10 w-auto" />
            <div className="hidden sm:block">
              <div className="text-sm font-bold tracking-wider text-[var(--color-text-light)] leading-none font-body">APISDESIGN</div>
              <div className="text-[10px] tracking-[0.12em] text-[var(--color-text-light-muted)] uppercase font-body">strony | design | marketing</div>
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
            <div
              className="relative"
              onMouseEnter={() => setOfertaOpen(true)}
              onMouseLeave={() => setOfertaOpen(false)}
            >
              <button className="nav-link text-[var(--color-text-light-muted)] hover:text-[var(--color-text-light)] flex items-center gap-1">
                Oferta <ChevronDown className="w-3 h-3" />
              </button>
              <AnimatePresence>
                {ofertaOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    transition={{ duration: 0.2 }}
                    className="absolute top-full left-0 mt-3 w-56 bg-[#1a1917] border border-[var(--color-border-dark)] rounded overflow-hidden shadow-2xl z-[100]"
                  >
                    {ofertaItems.map((item) => (
                      <Link
                        key={item.href}
                        to={item.href}
                        className="block px-5 py-3 text-sm font-body text-[var(--color-text-light-muted)] hover:text-[var(--color-text-light)] hover:bg-[var(--color-charcoal)] transition-colors"
                      >
                        {item.name}
                      </Link>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {mainNav.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className={`nav-link transition-colors ${
                  location.pathname === item.href
                    ? "text-[var(--color-text-light)]"
                    : "text-[var(--color-text-light-muted)] hover:text-[var(--color-text-light)]"
                }`}
              >
                {item.name}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-4">
            <Link to="/kontakt" className="hidden lg:block">
              <button className="button-primary" data-testid="header-cta">
                Zamów projekt
              </button>
            </Link>

            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="lg:hidden relative z-50 p-2"
              data-testid="mobile-menu-btn"
            >
              {menuOpen ? <X className="w-6 h-6 text-[var(--color-text-light)]" /> : <Menu className="w-6 h-6 text-[var(--color-text-light)]" />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-[var(--color-black)] overflow-y-auto"
          >
            <div className="min-h-full flex flex-col px-6 pt-16 pb-6">
              <nav className="flex-1">
                <p className="text-[10px] font-body font-bold uppercase tracking-[0.15em] text-[var(--color-gold)] mb-3">Oferta</p>
                {ofertaItems.map((item, idx) => (
                  <motion.div key={item.href} initial={{ opacity: 0, x: -15 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: idx * 0.03 }}>
                    <Link to={item.href} className="block text-sm font-body text-[var(--color-text-light-muted)] hover:text-[var(--color-gold)] py-1.5 transition-colors" onClick={() => setMenuOpen(false)}>{item.name}</Link>
                  </motion.div>
                ))}
                <div className="border-t border-[var(--color-border-dark)] mt-4 pt-4">
                  {mainNav.map((item, idx) => (
                    <motion.div key={item.href} initial={{ opacity: 0, x: -15 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.15 + idx * 0.03 }}>
                      <Link to={item.href} className="block font-body text-base font-semibold text-[var(--color-text-light)] hover:text-[var(--color-gold)] py-2 transition-colors" onClick={() => setMenuOpen(false)}>{item.name}</Link>
                    </motion.div>
                  ))}
                </div>
              </nav>
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }} className="mt-4 space-y-2.5">
                <Link to="/kontakt" onClick={() => setMenuOpen(false)}>
                  <button className="button-primary w-full justify-center text-sm">Zamów projekt</button>
                </Link>
                <a href="https://wa.me/48507334540" target="_blank" rel="noopener noreferrer" onClick={() => setMenuOpen(false)} className="block">
                  <button className="button-outline w-full justify-center flex items-center gap-2 text-sm">
                    <WhatsAppIcon className="w-4 h-4" /> WhatsApp
                  </button>
                </a>
                <div className="flex justify-between pt-1 text-[var(--color-text-light-muted)] text-xs font-body">
                  <a href="mailto:aml01@vp.pl" className="hover:text-[var(--color-gold)]">aml01@vp.pl</a>
                  <a href="tel:+48507334540" className="hover:text-[var(--color-gold)]">+48 507 334 540</a>
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

const Footer = () => {
  return (
    <footer className="bg-[var(--color-black)] border-t border-[var(--color-border-dark)]" data-testid="main-footer">
      <div className="container">
        <div className="py-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Col 1: Logo + desc */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <img src={LOGO_URL} alt="ApisDesign" className="h-9" />
              <div>
                <div className="text-sm font-bold tracking-wider text-[var(--color-text-light)] font-body">APISDESIGN</div>
                <div className="text-[9px] tracking-[0.1em] text-[var(--color-text-light-muted)] uppercase font-body">strony | design | marketing</div>
              </div>
            </div>
            <p className="text-xs text-[var(--color-text-light-muted)] font-body leading-relaxed">
              Tworzymy strony internetowe, grafiki<br />
              i identyfikacje wizualne, które budują wizerunek<br />
              i wspierają rozwój Twojej marki.
            </p>
          </div>

          {/* Col 2: Kontakt */}
          <div>
            <h4 className="text-xs font-body font-bold uppercase tracking-wider text-[var(--color-text-light)] mb-4">Kontakt</h4>
            <div className="space-y-2.5">
              <a href="mailto:aml01@vp.pl" className="text-xs font-body text-[var(--color-text-light-muted)] hover:text-[var(--color-gold)] transition-colors flex items-center gap-2">
                <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" /></svg>
                aml01@vp.pl
              </a>
              <a href="tel:+48507334540" className="text-xs font-body text-[var(--color-text-light-muted)] hover:text-[var(--color-gold)] transition-colors flex items-center gap-2">
                <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" /></svg>
                +48 507 334 540
              </a>
              <a href="https://wa.me/48507334540" target="_blank" rel="noopener noreferrer" className="text-xs font-body text-[var(--color-text-light-muted)] hover:text-[var(--color-gold)] transition-colors flex items-center gap-2">
                <WhatsAppIcon className="w-3.5 h-3.5 flex-shrink-0" />
                WhatsApp
              </a>
              <p className="text-xs font-body text-[var(--color-text-light-muted)] flex items-center gap-2">
                <span className="w-3.5 h-3.5 flex-shrink-0" />
                Ania — ApisDesign
              </p>
            </div>
          </div>

          {/* Col 3: Obserwuj nas */}
          <div>
            <h4 className="text-xs font-body font-bold uppercase tracking-wider text-[var(--color-text-light)] mb-4">Obserwuj nas</h4>
            <div className="flex gap-3">
              <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-[var(--color-charcoal)] flex items-center justify-center text-[var(--color-text-light-muted)] hover:text-[var(--color-text-light)] hover:bg-[var(--color-border-dark)] transition-all" data-testid="footer-facebook">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
              </a>
              <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-[var(--color-charcoal)] flex items-center justify-center text-[var(--color-text-light-muted)] hover:text-[var(--color-text-light)] hover:bg-[var(--color-border-dark)] transition-all" data-testid="footer-instagram">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
              </a>
            </div>
          </div>

          {/* Col 4: Informacje */}
          <div>
            <h4 className="text-xs font-body font-bold uppercase tracking-wider text-[var(--color-text-light)] mb-4">Informacje</h4>
            <div className="space-y-2">
              <Link to="/polityka-prywatnosci" className="block text-xs font-body text-[var(--color-text-light-muted)] hover:text-[var(--color-gold)] transition-colors">
                &rsaquo; Polityka prywatności
              </Link>
              <Link to="/regulamin" className="block text-xs font-body text-[var(--color-text-light-muted)] hover:text-[var(--color-gold)] transition-colors">
                &rsaquo; Regulamin
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Copyright bar */}
      <div className="border-t border-[var(--color-border-dark)]">
        <div className="container py-5">
          <p className="text-[11px] font-body text-[var(--color-text-light-muted)] text-center">
            Copyright &copy; 2025-2026 ApisDesign. All rights reserved. Designed with ApisDesign.
          </p>
        </div>
      </div>
    </footer>
  );
};

const Layout = () => {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <div className="min-h-screen flex flex-col bg-[var(--color-black)]">
      <Header />
      <motion.main
        key={location.pathname}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
        className="flex-1"
      >
        <Outlet />
      </motion.main>
      <Footer />
    </div>
  );
};

export default Layout;
